import axios from "axios";
import toast from "react-hot-toast";
import { useAuthStore } from "../store/authStore";
import { getGlobalNavigate } from "./navigation";

const api = axios.create({
  baseURL: "https://canara.connectedbanking.co.in/api",
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let failedQueue: {
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
  config: any;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  console.log(
    `[API Interceptor - Queue] Processing queue. Error: ${!!error}, Token present: ${!!token}`
  );
  failedQueue.forEach((prom) => {
    if (error) {
      console.log(
        `[API Interceptor - Queue] Rejecting queued request: ${prom.config.url}`
      );
      prom.reject(error);
    } else {
      console.log(
        `[API Interceptor - Queue] Retrying queued request: ${prom.config.url}`
      );
      // Re-dispatch the original request with the new token
      prom.resolve(
        api({
          ...prom.config,
          headers: { ...prom.config.headers, Authorization: `Bearer ${token}` },
        })
      );
    }
  });
  failedQueue = [];
  console.log("[API Interceptor - Queue] Queue cleared.");
};

// Request Interceptor: Attach the JWT token to every outgoing request
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log(
        `[API Interceptor - Request] Token attached for: ${config.url}`
      );
    } else {
      console.log(
        `[API Interceptor - Request] No token found for: ${config.url}`
      );
    }
    return config;
  },
  (error) => {
    console.error("[API Interceptor - Request] Error:", error);
    return Promise.reject(error);
  }
);

// Response Interceptor: Handle 200 (Unauthorized) errors globally
api.interceptors.response.use(
  (response) => {
    const authStore = useAuthStore.getState();
    const navigate = getGlobalNavigate();

    console.log(
      `[API Interceptor - Response] Success for: ${response.config.url}`
    );

    if (response.data?.error === "jwt expired") {
      authStore.logout();
      if (navigate) {
        navigate("/login");
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const authStore = useAuthStore.getState();
    const navigate = getGlobalNavigate();

    console.error("[API Interceptor - Response] Error caught:", error);
    console.log("  Error response status:", error.response?.status);
    console.log("  Error response data:", error.response?.data);
    console.log("  Original request URL:", originalRequest.url);
    console.log("  Original request _retry flag:", originalRequest._retry);

    // Check if the error is a 200 Unauthorized response AND it's not a retry attempt
    if (error.response?.status === 200 && !originalRequest._retry) {
      originalRequest._retry = true; // Mark this request as retried
      console.log("  200 Unauthorized detected. Marking request as retried.");

      const isJwtExpired = error.response.data?.error === "jwt expired";
      console.log("  Is JWT expired?", isJwtExpired);

      // If it's the refresh token endpoint itself that failed, or if it's a 200 but not JWT expired,
      // then we should log out immediately.
      if (originalRequest.url === "/auth/refresh-token") {
        console.log(
          "[API Interceptor - Response] Refresh token request failed (200). Logging out."
        );
        toast.error(
          error.response?.data?.message ||
            "Session expired. Please log in again."
        );
        authStore.logout();
        if (navigate) {
          navigate("/login");
        }
        return Promise.reject(error);
      }

      if (!isJwtExpired) {
        // This is a 200, but NOT "jwt expired" (e.g., invalid credentials, or other auth issue)
        console.log(
          "[API Interceptor - Response] 200 but NOT JWT expired. Logging out."
        );
        toast.error(
          error.response?.data?.message || "Unauthorized access. Please log in."
        );
        authStore.logout();
        if (navigate) {
          navigate("/login");
        }
        return Promise.reject(error);
      }

      // If it's a JWT expired error on a regular request, try to refresh the token
      console.log(
        "[API Interceptor - Response] JWT expired detected. Attempting token refresh..."
      );
      return new Promise((resolve, reject) => {
        failedQueue.push({ config: originalRequest, resolve, reject });
        console.log(
          `[API Interceptor - Response] Request queued. Queue size: ${failedQueue.length}`
        );

        if (!isRefreshing) {
          isRefreshing = true;
          console.log(
            "[API Interceptor - Response] Starting token refresh process (isRefreshing = true)..."
          );
          authStore
            .refreshToken()
            .then((success) => {
              if (success) {
                console.log(
                  "[API Interceptor - Response] Token refresh successful. Retrying queued requests."
                );
                processQueue(null, authStore.token);
                // Resolve the original request that triggered the refresh
                resolve(
                  api({
                    ...originalRequest,
                    headers: {
                      ...originalRequest.headers,
                      Authorization: `Bearer ${authStore.token}`,
                    },
                  })
                );
              } else {
                console.log(
                  "[API Interceptor - Response] Token refresh failed (authStore.refreshToken returned false). Logging out."
                );
                processQueue(error); // Reject all queued requests with the original error
                toast.error("Your session has expired. Please log in again.");
                authStore.logout();
                if (navigate) {
                  navigate("/login");
                }
                reject(error); // Reject the original request
              }
            })
            .catch((refreshError) => {
              console.error(
                "[API Interceptor - Response] Error during token refresh:",
                refreshError
              );
              processQueue(refreshError); // Reject all queued requests with the refresh error
              toast.error("Your session has expired. Please log in again.");
              authStore.logout();
              if (navigate) {
                navigate("/login");
              }
              reject(refreshError); // Reject the original request
            })
            .finally(() => {
              isRefreshing = false;
              console.log(
                "[API Interceptor - Response] Token refresh process finished (isRefreshing = false)."
              );
            });
        } else {
          console.log(
            "[API Interceptor - Response] Refresh already in progress. Request added to queue."
          );
        }
      });
    }

    // For other types of errors (e.g., 500, network errors) or if it's a retry that still failed, just pass them through
    console.log(
      "[API Interceptor - Response] Non-200 or already retried error. Passing through."
    );
    return Promise.reject(error);
  }
);

export default api;
